import _ from 'lodash'

export interface MultipleChoiceAlternative {
  name: string
  is_correct?: boolean
}

export interface MultipleChoiceSelectionStatus {
  /** Quantas alternativas a questao exige. Sempre >= 1 quando ha alternativas. */
  requiredCount: number
  /** Quantas alternativas distintas o aluno marcou. */
  selectedCount: number
  /** Questao de multipla resposta: exige duas ou mais alternativas. */
  isMultiple: boolean
  /** A selecao atingiu a quantidade exigida e pode ser verificada. */
  isComplete: boolean
  /** Quantas alternativas ainda faltam para completar a selecao. */
  missingCount: number
}

const SINGULAR_MESSAGE = 'Hmmm... acho que faltou selecionar mais uma alternativa.'
const PLURAL_MESSAGE =
  'Essa questão possui mais de uma resposta. Selecione todas as alternativas antes de continuar.'

function countSelected(selectedAnswer: string | string[] | null): number {
  if (Array.isArray(selectedAnswer)) {
    return _.uniq(selectedAnswer.filter(name => name?.trim() !== '')).length
  }
  return selectedAnswer?.trim() ? 1 : 0
}

/**
 * Descreve o estado da selecao de uma questao de multipla escolha.
 *
 * A quantidade exigida vem das alternativas marcadas como corretas, deduplicadas
 * por nome exatamente como o componente que as renderiza faz — se as duas contagens
 * divergirem, o aluno fica preso num botao que nunca libera.
 */
export function getMultipleChoiceSelectionStatus(
  alternatives: MultipleChoiceAlternative[],
  selectedAnswer: string | string[] | null
): MultipleChoiceSelectionStatus {
  const uniqueAlternatives = _.uniqBy(alternatives ?? [], 'name')
  const correctCount = uniqueAlternatives.filter(
    alternative => alternative.is_correct === true
  ).length

  // Questao sem alternativas tem fluxo proprio de avanco e nao deve ser travada aqui.
  if (uniqueAlternatives.length === 0) {
    return {
      requiredCount: 0,
      selectedCount: 0,
      isMultiple: false,
      isComplete: true,
      missingCount: 0
    }
  }

  // Sem correta configurada (pesquisa, reflexao), qualquer marcacao ja completa.
  const requiredCount = Math.max(correctCount, 1)
  const isMultiple = correctCount >= 2
  const selectedCount = countSelected(selectedAnswer)
  const missingCount = Math.max(requiredCount - selectedCount, 0)

  return {
    requiredCount,
    selectedCount,
    isMultiple,
    isComplete: missingCount === 0,
    missingCount
  }
}

/**
 * Mensagem amigavel exibida quando o aluno tenta avancar com a selecao incompleta.
 */
export function getIncompleteSelectionMessage(
  missingCount: number
): string | null {
  if (missingCount <= 0) return null
  return missingCount === 1 ? SINGULAR_MESSAGE : PLURAL_MESSAGE
}
